import { StorageService } from '../../../../shared/services/storage.service';
import { VendorHandler } from '@portal/app/dashboard/integrations/shared/vendor-handlers/vendor-handler';
import {
  IOAuthDetails,
  IVendorConnectConfirmation,
  IVendorConnectInitRequest,
  IVendorConnectInitResponse
} from '@portal/app/dashboard/integrations/models/vendor/VendorIntegrationModels';
import { IVendorInitiateRequest } from '@portal/app/dashboard/integrations/models/vendor/IVendorInitiateRequest';
import {
  callbackDomain,
  VendorIdSource
} from '@portal/app/dashboard/integrations/shared/constants';
import { IVendorConfigurationRequest } from '@portal/app/dashboard/integrations/models/vendor/IVendorConfigurationRequest';
import { map } from 'lodash-es';
import { IVendorOAuthConfirmationRequest } from '@portal/app/dashboard/integrations/models/vendor/IVendorOAuthConfirmationRequest';
import { IVendorReConfigurationRequest } from '@portal/app/dashboard/integrations/models/vendor/IVendorReConfigurationRequest';
import { WindowService } from '@portal/app/shared/services/window.service';

export class AmazonSellerHandler implements VendorHandler {
  private static readonly REGION_CONFIG = {
    'NA': {
      authUrl: 'https://sellercentral.amazon.com/apps/authorize/consent',
      apiUrl: 'https://sellingpartnerapi-na.amazon.com',
      label: 'North America (US, CA, MX, BR)'
    },
    'EU': {
      authUrl: 'https://sellercentral-europe.amazon.com/apps/authorize/consent', 
      apiUrl: 'https://sellingpartnerapi-eu.amazon.com',
      label: 'Europe (UK, DE, FR, IT, ES, +)'
    },
    'FE': {
      authUrl: 'https://sellercentral.amazon.co.jp/apps/authorize/consent',
      apiUrl: 'https://sellingpartnerapi-fe.amazon.com',
      label: 'Far East (JP, AU, SG)'
    }
  };

  constructor(
    private storageService: StorageService,
    private readonly windowService: WindowService
  ) {}

  static getAvailableRegions() {
    return Object.keys(AmazonSellerHandler.REGION_CONFIG).map(regionKey => {
      const config = AmazonSellerHandler.REGION_CONFIG[regionKey as keyof typeof AmazonSellerHandler.REGION_CONFIG];
      return {
        id: regionKey,
        label: config.label,
        authUrl: config.authUrl,
        apiUrl: config.apiUrl
      };
    });
  }

  prepareRequestObjectToInitiateVendor(
    vendorDetails: IVendorConnectInitRequest
  ): IVendorInitiateRequest {
    /* eslint-disable no-console */
    console.log(`Amazon Seller initiation data: ${vendorDetails}`);
    
    const region = vendorDetails['region'] || 'NA';

    return {
      req: {},
      queryParams: {
        callbackDomain: callbackDomain(),
        region
      },
      pathParams: { vendorId: 'amazon-seller' }
    };
  }

  handleVendorInitiateResponse(data: IVendorConnectInitResponse): void {
    const url = new URL(
      (data.vendorCredentialsDetails as IOAuthDetails).redirectURL
    );
    const params = new URLSearchParams(url.search);

    localStorage.setItem('appState', params.get('state') || '');
    localStorage.setItem(
      'callBackParams',
      JSON.stringify(
        (data.vendorCredentialsDetails as IOAuthDetails).callbackParameters
      )
    );
    this.windowService.navigateToHref(
      (data.vendorCredentialsDetails as IOAuthDetails).redirectURL
    );
  }

  prepareRequestToConfirmVendor(): IVendorOAuthConfirmationRequest {
    return {
      code: `${localStorage.getItem('spapi_oauth_code')}`,
      state: `${localStorage.getItem('state')}`,
      callbackDomain: callbackDomain()
    };
  }

  prepareRequestObjectToConfigureVendor(
    accounts: {
      accountName: string;
      accountId: string;
      clientAccountName?: string;
      managerId?: string;
      disabled: boolean;
      endpoint?: string;
    }[]
  ): IVendorConfigurationRequest {
    return {
      vendorId: 'amazon-seller',
      vendorData: map(accounts, (account) => ({
        accountId: account.accountId,
        accountName: account.accountName,
        endpoint: account.endpoint ? account.endpoint : ''
      }))
    };
  }

  confirmVendor(data: IVendorConnectConfirmation): void {
    const jsonData = data.accountDetails
      ? JSON.stringify(data.accountDetails.vendorAccounts)
      : '';
    this.storageService.setIntegrationAccountDetails(
      VendorIdSource['amazon-seller'],
      jsonData
    );
  }

  prepareRequestObjectToReConfigureVendor(
    accounts: {
      accountName: string;
      accountId: string;
      managerId?: string | undefined;
      disabled: boolean;
      endpoint?: string;
      clientAccountName?: string;
    }[],
    dsInstanceIds: number[]
  ): IVendorReConfigurationRequest {
    return {
      vendorId: 'amazon-seller',
      vendorData: map(accounts, (account) => ({
        accountId: account.accountId,
        accountName: account.accountName,
        endpoint: account.endpoint ? account.endpoint : ''
      })),
      dsInstanceIds
    };
  }
}
